import { useState } from "react";
import { toast } from "sonner";
import { ListPlus, Mailbox, Send, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useFetch } from "../../lib/useFetch";

export const InviteButton = ({ e, inline = false }) => {
  const [open, setOpen] = useState(false);
  const cls = inline ? "inline-flex items-center gap-1 rounded-full bg-[#FFC107] px-3 py-1 text-xs font-bold text-[#0A2540] hover:brightness-95"
    : "absolute right-4 top-14 z-20 inline-flex items-center gap-1.5 rounded-full bg-[#FFC107] px-3 py-2 text-xs font-extrabold uppercase tracking-wider text-[#0A2540] shadow-lg ring-2 ring-white transition-transform hover:scale-105";
  return (<>
    <button onClick={(x) => { x.preventDefault(); x.stopPropagation(); setOpen(true); }} title="Invite guests" aria-label="Invite guests"
      className={cls} data-testid={`invite-button-${e.id}${inline ? "-admin" : ""}`}><Mailbox size={inline ? 14 : 16} />Invite</button>
    {open && <InviteDialog e={e} onClose={() => setOpen(false)} />}
  </>);
};

const BulkInvite = ({ e, reload }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${e.id}/invites/bulk`, { text });
      setRes(data);
      toast.success(`${data.invited.length} invitation(s) sent`);
      if (!data.invalid.length && !data.already_invited.length) setText("");
      reload();
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-sm font-semibold text-[#00A8B5]" data-testid="invite-bulk-open"><ListPlus size={16} />Paste a list of emails</button>;
  return (
    <div className="space-y-2 rounded-2xl bg-slate-50 p-3" data-testid="invite-bulk">
      <p className="text-xs text-slate-500">Paste emails separated by new lines, commas, semicolons or spaces ("Name &lt;email&gt;" also works). Each new guest receives the normal invitation email.</p>
      <textarea value={text} onChange={(x) => { setText(x.target.value); setRes(null); }} rows={5} placeholder={"anna@example.com\nluca@example.com, marta@example.com"} className="w-full rounded-xl border border-slate-300 p-3 text-sm" data-testid="invite-bulk-text" />
      <div className="flex items-center gap-3">
        <button disabled={busy || !text.trim()} onClick={go} className="btn-buoy !py-2 disabled:opacity-40" data-testid="invite-bulk-send"><Send size={14} />{busy ? "Sending…" : "Invite all"}</button>
        <button onClick={() => { setOpen(false); setRes(null); }} className="text-sm font-semibold text-slate-500" data-testid="invite-bulk-cancel">Close</button>
      </div>
      {res && <div className="space-y-1 text-xs" data-testid="invite-bulk-result">
        <p className="font-semibold text-emerald-700" data-testid="invite-bulk-invited">Invited: {res.invited.length}{res.invited.length ? ` (${res.invited.map((i) => i.email).join(", ")})` : ""}</p>
        {res.already_invited.length > 0 && <p className="text-amber-700" data-testid="invite-bulk-already">Already invited, skipped: {res.already_invited.join(", ")}</p>}
        {res.invalid.length > 0 && <p className="text-rose-600" data-testid="invite-bulk-invalid">Not valid, skipped: {res.invalid.join(", ")}</p>}
      </div>}
    </div>
  );
};

const InviteDialog = ({ e, onClose }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: invites, reload } = useFetch(`/admin/events/${e.id}/invites`);
  const { data: hits } = useFetch(q.trim().length >= 2 ? `/admin/user-emails?q=${encodeURIComponent(q.trim())}` : null);
  const invite = async (email) => {
    setBusy(true);
    try { await api.post(`/admin/events/${e.id}/invites`, { email }); toast.success(`Invitation sent to ${email}`); setQ(""); reload(); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  const remove = async (i) => {
    if (!window.confirm(`Remove the invitation for ${i.email}? The event will no longer be visible to this person.`)) return;
    try { await api.delete(`/admin/events/${e.id}/invites/${i.id}`); reload(); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl" data-testid="invite-dialog" onClick={(x) => x.stopPropagation()}>
        <DialogHeader><DialogTitle className="font-display text-2xl">Invite to {e.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Search a user by email or name, or type any email (not yet registered people get an invitation to create their account).</p>
        <div className="flex gap-2">
          <Input value={q} onChange={(x) => setQ(x.target.value)} placeholder="Email or name…" className="h-11 rounded-xl" data-testid="invite-search" />
          <button disabled={busy || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(q.trim())} onClick={() => invite(q.trim())} className="btn-buoy !px-4 disabled:opacity-40" data-testid="invite-send-typed"><Send size={16} /></button>
        </div>
        {hits?.length > 0 && <div className="space-y-1" data-testid="invite-results">{hits.map((h) => {
          const done = (invites || []).some((i) => i.email === h.email.toLowerCase());
          return (
          <button key={h.email} disabled={busy || done} onClick={() => invite(h.email)} className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-left text-sm hover:border-[#00A8B5] disabled:opacity-50" data-testid={`invite-result-${h.email}`}>
            <span><b>{h.name}</b> · {h.email}</span>{done ? <span className="text-xs font-semibold text-emerald-600">Invited</span> : <Send size={14} className="text-[#00A8B5]" />}</button>);
        })}</div>}
        <BulkInvite e={e} reload={reload} />
        <div className="space-y-2 border-t border-slate-100 pt-4" data-testid="invite-list">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Invited ({invites?.length || 0})</p>
          {(invites || []).map((i) => (
            <div key={i.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`invite-row-${i.email}`}>
              <span>{i.name && <b>{i.name} · </b>}{i.email}<span className="block text-xs text-slate-500">{i.registered ? "Registered" : "Not registered yet"} · {fmtDate(i.invited_at)}</span></span>
              <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-600" data-testid={`invite-remove-${i.email}`}><Trash2 size={15} /></button>
            </div>))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
